import { Collapse, Form, FormInstance, Space, Switch, Typography } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useWatch } from 'antd/es/form/Form'
import { DeliverySubscription } from '../../../types/deliverySubscription/DeliverySubscription'
import useCustomReport from '../../customReport/queries/report/useCustomReport'
import { CustomReportArgument } from '../../../redux/context/customReports/types'
import CustomReportTreeSelect from '../../customReport/components/treeSelect/CustomReportTreeSelect'
import { CompanyDimensionPicker, CompanySelection } from '../../../shared/components/custom/CompanyDimensionPicker'
import {
  mergeArgumentsToPickerValue,
  splitPickerToCompanyArgValues,
  splitPickerToDimensionArgValues
} from '../../../pages/reporting/custom/components/argument/argumentUtils'
import { Company } from '../../../types/company/Company'
import { useDeliverySubscriptionForm } from '../hooks/useDeliverySubscriptionForm'
import { DeliverySubscriptionFormData } from '../types'
import SharedSubscriptionFields from './SharedSubscriptionFields'

const INITIATOR_OPTIONS = ['monthly', 'weekly', 'daily', 'periodLock', 'manual'] as const

interface CustomReportSubscriptionFormProps {
  form: FormInstance<DeliverySubscriptionFormData>
  deliverySubscription?: DeliverySubscription
}

interface ArgumentPickerProps {
  reportArguments: CustomReportArgument[]
  companies: Company[]
  value?: CustomReportArgument[]
  onChange?: (args: CustomReportArgument[]) => void
  disabled?: boolean
}

const ArgumentPicker: React.FC<ArgumentPickerProps> = ({
  reportArguments,
  companies,
  value = [],
  onChange,
  disabled
}) => {
  const companyArg = reportArguments.find(a => a.type === 'company')
  const dimensionArg = reportArguments.find(a => a.type === 'dimension')

  const pickerValue = mergeArgumentsToPickerValue(
    value.find(a => a.type === 'company'),
    value.find(a => a.type === 'dimension'),
    companies
  )

  const handleChange = (selections: CompanySelection[]) => {
    const args: CustomReportArgument[] = []
    if (companyArg) args.push({ ...companyArg, values: splitPickerToCompanyArgValues(selections) })
    if (dimensionArg) args.push({ ...dimensionArg, values: splitPickerToDimensionArgValues(selections) })
    onChange?.(args)
  }

  return (
    <CompanyDimensionPicker
      companies={companies.map(c => ({ id: c.id, name: c.name }))}
      value={pickerValue}
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

const CustomReportSubscriptionForm: React.FC<CustomReportSubscriptionFormProps> = ({ form, deliverySubscription }) => {
  const { t } = useTranslation()
  const { selectableCompanies, selectableUsers, companyId } = useDeliverySubscriptionForm(form, deliverySubscription)

  const reportId = useWatch('reportId', form)
  const printVersion = useWatch(['params', 'printVersion'], form)
  const { data: customReport } = useCustomReport(reportId, companyId)

  return (
    <Form
      onValuesChange={changedValue => {
        if (changedValue.companyId) {
          form.resetFields(['userId', 'reportId', 'params', 'recipients'])
        }
      }}
      form={form}
      layout="vertical"
      className="delivery-subscription-form"
    >
      <SharedSubscriptionFields
        form={form}
        initiatorOptions={INITIATOR_OPTIONS}
        selectableCompanies={selectableCompanies}
        selectableUsers={selectableUsers}
        showRecipients
      />

      <Form.Item
        label={t('global:report')}
        name="reportId"
        rules={[{ required: true, message: t('global:required-field') }]}
      >
        <CustomReportTreeSelect companyId={companyId} style={{ width: '100%' }} />
      </Form.Item>

      {customReport?.args && (
        <Collapse
          size="small"
          style={{ marginBottom: 24 }}
          defaultActiveKey={deliverySubscription?.params?.args?.length ? ['args'] : []}
          items={[
            {
              key: 'args',
              label: t('global:arguments'),
              children: (
                <Form.Item name={['params', 'args']} noStyle>
                  <ArgumentPicker reportArguments={customReport.args} companies={selectableCompanies} />
                </Form.Item>
              )
            }
          ]}
        />
      )}

      {deliverySubscription && (deliverySubscription.params?.printVersion ?? 'v1') === 'v1' && (
        <Form.Item name={['params', 'printVersion']} label={t('subscriptionsPage:print-settings')}>
          <Space>
            <Switch
              size="small"
              disabled={!customReport}
              checked={printVersion === 'v2'}
              onChange={checked => form.setFieldValue(['params', 'printVersion'], checked ? 'v2' : 'v1')}
            />
            <Typography.Text>{t('subscriptionsPage:use-new-print')}</Typography.Text>
          </Space>
        </Form.Item>
      )}
    </Form>
  )
}

export default CustomReportSubscriptionForm
